import { useEffect, useRef, useState } from "react";
import { api, apiUpload, currentUser, downloadDocument } from "../lib/api";
const DOC_TYPES = [
    "CUSTOMER_DOCUMENT",
    "CHEQUE_COPY",
    "LEGAL_NOTICE",
    "COURT_DOCUMENT",
    "JUDGMENT_COPY",
    "LAWYER_CORRESPONDENCE",
    "SETTLEMENT_DOCUMENT",
    "PAYMENT_RECEIPT",
    "SUPPORTING_EVIDENCE",
    "OTHER",
];
export function DocumentsPanel({ caseId }) {
    const [items, setItems] = useState([]);
    const [documentType, setDocumentType] = useState(DOC_TYPES[0]);
    const [remarks, setRemarks] = useState("");
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInput = useRef(null);
    const [editingId, setEditingId] = useState(null);
    const [editRemarks, setEditRemarks] = useState("");
    const [busyId, setBusyId] = useState(null);
    const user = currentUser();
    function load() {
        api
            .get(`/cases/${caseId}/documents`)
            .then((r) => setItems(r.items))
            .catch(() => setError("Couldn't load documents."));
    }
    useEffect(load, [caseId]);
    function canManage(d) {
        return user?.role === "MANAGEMENT" || user?.id === d.uploadedBy;
    }
    function startEdit(d) {
        setEditingId(d.id);
        setEditRemarks(d.remarks ?? "");
    }
    async function saveRemarks(d) {
        setBusyId(d.id);
        setError(null);
        try {
            await api.put(`/cases/${caseId}/documents/${d.id}`, { remarks: editRemarks || undefined });
            setEditingId(null);
            load();
        }
        catch {
            setError("Couldn't save the remarks.");
        }
        finally {
            setBusyId(null);
        }
    }
    async function remove(d) {
        if (!window.confirm(`Remove "${d.fileName}"? This can't be undone.`))
            return;
        setBusyId(d.id);
        setError(null);
        try {
            await api.delete(`/cases/${caseId}/documents/${d.id}`);
            load();
        }
        catch {
            setError("Couldn't remove the document.");
        }
        finally {
            setBusyId(null);
        }
    }
    async function upload() {
        const file = fileInput.current?.files?.[0];
        if (!file) {
            setError("Choose a file first.");
            return;
        }
        setError(null);
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("documentType", documentType);
            if (remarks)
                form.append("remarks", remarks);
            await apiUpload(`/cases/${caseId}/documents`, form);
            setRemarks("");
            if (fileInput.current)
                fileInput.current.value = "";
            load();
        }
        catch {
            setError("Upload failed — accepted types are PDF, PNG, JPEG, WebP, up to 15MB.");
        }
        finally {
            setUploading(false);
        }
    }
    return (<div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <input ref={fileInput} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" style={{ fontSize: "0.82rem" }}/>
        <select value={documentType} onChange={(e) => setDocumentType(e.target.value)} style={selectStyle}>
          {DOC_TYPES.map((t) => (<option key={t} value={t}>
              {t.replace(/_/g, " ")}
            </option>))}
        </select>
        <input placeholder="Remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: 140 }}/>
        <button onClick={upload} disabled={uploading} style={btnStyle}>
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>

      {error && <div style={{ color: "var(--st-risk)", fontSize: "0.85rem", marginBottom: 12 }}>{error}</div>}

      {items.map((d) => (<div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: "1px solid var(--line-soft)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.86rem", fontWeight: 600 }}>{d.fileName}</div>
            <div className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-faint)" }}>
              {d.documentType.replace(/_/g, " ")} · {new Date(d.uploadedAt).toLocaleString()}
              {d.remarks && editingId !== d.id ? ` · ${d.remarks}` : ""}
            </div>
            {editingId === d.id && (<div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <input value={editRemarks} onChange={(e) => setEditRemarks(e.target.value)} placeholder="Remarks" style={{ ...selectStyle, flex: 1, minWidth: 140 }}/>
                <button onClick={() => saveRemarks(d)} disabled={busyId === d.id} style={linkBtn}>
                  Save
                </button>
                <button onClick={() => setEditingId(null)} style={linkBtn}>
                  Cancel
                </button>
              </div>)}
          </div>
          <button onClick={() => downloadDocument(caseId, d.id, d.fileName)} style={linkBtn}>
            Download
          </button>
          {canManage(d) && editingId !== d.id && (<>
              <button onClick={() => startEdit(d)} style={linkBtn}>
                Remarks
              </button>
              <button onClick={() => remove(d)} disabled={busyId === d.id} style={{ ...linkBtn, color: "var(--st-risk)" }}>
                Remove
              </button>
            </>)}
        </div>))}
      {items.length === 0 && !error && <div style={{ color: "var(--ink-faint)", fontSize: "0.85rem" }}>No documents uploaded yet.</div>}
    </div>);
}
const selectStyle = {
    border: "1px solid var(--line)",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: "0.82rem",
    fontFamily: "inherit",
};
const btnStyle = {
    background: "var(--brand)",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.82rem",
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
};
const linkBtn = {
    background: "transparent",
    border: "1px solid var(--line)",
    color: "var(--accent-ink)",
    fontWeight: 600,
    fontSize: "0.78rem",
    padding: "6px 12px",
    borderRadius: 7,
    cursor: "pointer",
};
